import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCookie } from "../../contexts/CookieContext";
import { Button } from "../ui";

/**
 * Cookie consent banner component with customizable preferences
 * Allows users to accept all, reject optional, or customize cookie preferences for analytics and marketing
 *
 * @component
 */
const CookieBanner = () => {
  const { bannerVisible, acceptAllCookies, rejectOptionalCookies, setCustomPreferences, cookiePreferences } =
    useCookie();

  const [showPreferences, setShowPreferences] = useState(false);
  const [tempPreferences, setTempPreferences] = useState({
    analytics: cookiePreferences.analytics,
    marketing: cookiePreferences.marketing,
  });

  useEffect(() => {
    setTempPreferences({
      analytics: !!cookiePreferences.analytics,
      marketing: !!cookiePreferences.marketing,
    });
  }, [cookiePreferences.analytics, cookiePreferences.marketing, showPreferences]);

  if (!bannerVisible) return null;

  const handleAcceptAll = () => {
    acceptAllCookies();
  };

  const handleRejectAll = () => {
    rejectOptionalCookies();
  };

  const handleShowPreferences = () => {
    setShowPreferences(true);
  };

  const handleSavePreferences = () => {
    setCustomPreferences(tempPreferences);
    setShowPreferences(false);
  };

  const handlePreferenceChange = (type) => {
    setTempPreferences((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  return (
    <div className="cookie-banner">
      <div className="cookie-banner-content">
        {!showPreferences ? (
          // Main banner view
          <div className="cookie-banner-main">
            <div className="cookie-banner-text">
              <h3>Cookie Consent</h3>
              <p>
                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic.
                By clicking "Accept All", you consent to our use of cookies.
                <Link to="/privacy-policy" className="cookie-policy-link">
                  Read our Privacy Policy
                </Link>
              </p>
            </div>

            <div className="cookie-banner-actions">
              <Button variant="cookie-outline" onClick={handleRejectAll}>
                Reject Optional
              </Button>
              <Button variant="cookie-outline" onClick={handleShowPreferences}>
                Customize
              </Button>
              <Button variant="cookie-accept" onClick={handleAcceptAll}>
                Accept All
              </Button>
            </div>
          </div>
        ) : (
          // Preferences view
          <div className="cookie-banner-preferences">
            <div className="cookie-banner-text">
              <h3>Cookie Preferences</h3>
              <p>Choose which cookies you want to allow. You can change these settings at any time.</p>
            </div>

            <div className="cookie-preferences-list">
              {/* Necessary Cookies - Always enabled */}
              <div className="cookie-preference-item">
                <div className="cookie-preference-info">
                  <h4>Necessary Cookies</h4>
                  <p>Essential for the website to function properly. Cannot be disabled.</p>
                </div>
                <div className="cookie-preference-toggle">
                  <input type="checkbox" id="necessary" checked={true} disabled className="cookie-toggle" />
                  <label htmlFor="necessary" className="cookie-toggle-label disabled">
                    <span className="cookie-toggle-slider"></span>
                  </label>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="cookie-preference-item">
                <div className="cookie-preference-info">
                  <h4>Analytics Cookies</h4>
                  <p>Help us understand how visitors interact with our website.</p>
                </div>
                <div className="cookie-preference-toggle">
                  <input
                    type="checkbox"
                    id="analytics"
                    checked={tempPreferences.analytics}
                    onChange={() => handlePreferenceChange("analytics")}
                    className="cookie-toggle"
                  />
                  <label htmlFor="analytics" className="cookie-toggle-label">
                    <span className="cookie-toggle-slider"></span>
                  </label>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="cookie-preference-item">
                <div className="cookie-preference-info">
                  <h4>Marketing Cookies</h4>
                  <p>Used to deliver personalized advertisements and track campaign effectiveness.</p>
                </div>
                <div className="cookie-preference-toggle">
                  <input
                    type="checkbox"
                    id="marketing"
                    checked={tempPreferences.marketing}
                    onChange={() => handlePreferenceChange("marketing")}
                    className="cookie-toggle"
                  />
                  <label htmlFor="marketing" className="cookie-toggle-label">
                    <span className="cookie-toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            <div className="cookie-banner-actions">
              <Button variant="cookie-outline" onClick={() => setShowPreferences(false)}>
                Back
              </Button>
              <Button variant="cookie-accept" onClick={handleSavePreferences}>
                Save Preferences
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
